import { FileLock2, LogOut, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import ConceptualRenderingsGallery from "./ConceptualRenderingsGallery";
import InvestorPresentation from "./InvestorPresentation";
import PortalTermsAcceptance from "./PortalTermsAcceptance";

const BYTES_PER_KILOBYTE = 1024;

function DocumentLibrary({ documents, onDownload }) {
  if (!documents.length) {
    return (
      <div className="empty-state" data-testid="document-empty-state">
        No approved documents have been published to your library yet.
      </div>
    );
  }

  return (
    <div className="document-grid" data-testid="document-grid">
      {documents.map((fileRecord) => (
        <article
          data-testid={`portal-document-${fileRecord.document_id}`}
          key={fileRecord.document_id}
        >
          <FileLock2 size={25} />
          <span>{fileRecord.category}</span>
          <h3>{fileRecord.title}</h3>
          <p>
            {fileRecord.filename} · {Math.ceil(fileRecord.size / BYTES_PER_KILOBYTE)} KB
          </p>
          <button
            className="text-button"
            data-testid={`download-document-${fileRecord.document_id}`}
            onClick={() => onDownload(fileRecord)}
            type="button"
          >
            Download
          </button>
        </article>
      ))}
    </div>
  );
}

function PendingAccess() {
  return (
    <section className="pending-access">
      <FileLock2 size={34} />
      <h2>ACCESS UNDER REVIEW</h2>
      <p>
        Once approved, this portal will provide an executive summary, presentation
        materials, roadmap, conceptual renderings, research, and other relevant documents.
      </p>
    </section>
  );
}

function ApprovedPortalMaterials({ documents, onDownload, renderings }) {
  return (
    <>
      <section className="document-library">
        <div className="library-title">
          <div>
            <span className="eyebrow">Confidential library</span>
            <h2>REVIEW MATERIALS</h2>
          </div>
          <span className="watermark" data-testid="portal-watermark">
            Confidential — GAMEDAY Holdings LLC — Provided for Discussion Purposes Only
          </span>
        </div>
        <DocumentLibrary documents={documents} onDownload={onDownload} />
      </section>
      <InvestorPresentation />
      <ConceptualRenderingsGallery renderings={renderings} />
    </>
  );
}

function PortalBody({
  documents,
  onAcceptTerms,
  onDownload,
  renderings,
  terms,
  termsAccepted,
  termsState,
  approved,
}) {
  if (!approved) return <PendingAccess />;
  if (!termsAccepted && terms) {
    return (
      <PortalTermsAcceptance
        onAccept={onAcceptTerms}
        state={termsState}
        terms={terms}
      />
    );
  }
  return (
    <ApprovedPortalMaterials
      documents={documents}
      onDownload={onDownload}
      renderings={renderings}
    />
  );
}

export default function PortalDashboard({
  user,
  documents,
  renderings,
  terms,
  termsState,
  onAcceptTerms,
  onDownload,
  onLogout,
}) {
  const approved = user.access_status === "approved";
  const termsAccepted = user.terms_accepted_version === terms?.version;

  return (
    <main className="portal-page" data-testid="investor-portal">
      <section className="portal-header">
        <div>
          <span className="eyebrow">Secure investor portal</span>
          <h1>WELCOME, {user.name.toUpperCase()}.</h1>
          <p data-testid="portal-access-status">
            {approved
              ? "Your approved materials are available below."
              : "Your access request is pending review by the GAMEDAY Holdings team."}
          </p>
        </div>
        <div className="portal-controls">
          <span
            className={`status-pill ${approved ? "status-pill--approved" : ""}`}
            data-testid="portal-status-pill"
          >
            <ShieldCheck size={15} />
            {approved ? "Approved" : "Pending review"}
          </span>
          {user.role === "admin" && (
            <Link
              className="button button--outline"
              data-testid="admin-workspace-link"
              to="/admin"
            >
              Admin workspace
            </Link>
          )}
          <button
            className="icon-button"
            data-testid="portal-logout-button"
            onClick={onLogout}
            title="Sign out"
            type="button"
          >
            <LogOut size={19} />
          </button>
        </div>
      </section>
      <PortalBody
        approved={approved}
        documents={documents}
        onAcceptTerms={onAcceptTerms}
        onDownload={onDownload}
        renderings={renderings}
        terms={terms}
        termsAccepted={termsAccepted}
        termsState={termsState}
      />
    </main>
  );
}